/*
  Email capture, in place.

  Shopify's {% form 'customer' %} is a real POST: the browser leaves the page,
  Shopify redirects back, and the rebuilt page shows the confirmation. Two
  things went wrong with that on this theme.

  First, `form.posted_successfully?` is a property of the PAGE, not of the form
  you actually used — after any successful signup EVERY customer form on the
  page renders its success state. The footer's copy of that state carried
  `autofocus`, so the browser moved focus into the footer on load and dragged
  the whole page down with it. Subscribe from the Journal, land at the bottom
  of the site. (The autofocus attributes are gone now, so even the no-JS path
  no longer does this.)

  Second, and the reason for this file: the reload itself is the wrong shape
  for a one-field ask. Nobody submitting an email wants to lose their place in
  an article. So the post goes out over fetch and the answer is written into
  the form that asked — the field row steps aside, the section's own
  confirmation takes its place, and the page does not move a pixel.

  The contract, per form:
    form[data-sb-newsletter]        opt in
    [data-sb-newsletter-body]       hidden on success — marked on the boxes
                                    that already exist (field row, fine print)
                                    rather than on a wrapper, so no section's
                                    flex or grid gains a layer it didn't have
    [data-sb-newsletter-success]    the confirmation — always rendered, hidden
                                    until it is earned (by Liquid or by us)
    [data-sb-newsletter-error]      inline error — always rendered, hidden;
                                    we write the message Shopify returned

  Success is read from the theme's own markup rather than from the redirect
  URL's query string: the response is parsed and asked whether a success node
  came back un-hidden. That marker is ours, so it cannot drift the way
  `?customer_posted=true` can.

  Progressive enhancement throughout — if fetch fails outright the native
  submit is allowed to happen, because completing the signup matters more than
  staying on the page.
*/
(() => {
  const FORM = 'form[data-sb-newsletter]';
  const sending = new WeakSet();

  const parts = (form) => ({
    body: form.querySelectorAll('[data-sb-newsletter-body]'),
    ok: form.querySelector('[data-sb-newsletter-success]'),
    err: form.querySelector('[data-sb-newsletter-error]'),
  });

  function show(target, on) {
    if (!target) return;
    const list = target.nodeType ? [target] : Array.from(target);
    list.forEach((el) => {
      el.hidden = !on;
    });
  }

  function succeed(form) {
    const { body, ok, err } = parts(form);
    show(err, false);
    show(body, false);
    show(ok, true);
    form.classList.remove('is-sending');
    form.classList.add('is-subscribed');
    /* preventScroll is the whole point: the confirmation is announced and
       focusable, but the viewport stays exactly where the reader left it. */
    if (ok) {
      try {
        ok.focus({ preventScroll: true });
      } catch (e) {
        /* older Safari ignores the options bag — skip the focus rather than
           let it scroll */
      }
    }
    form.dispatchEvent(new CustomEvent('sb:subscribed', { bubbles: true }));
  }

  function fail(form, message) {
    const { err } = parts(form);
    form.classList.remove('is-sending');
    if (err && message) {
      err.textContent = message;
      show(err, true);
    }
    const field = form.querySelector('input[type="email"]');
    if (field) {
      field.setAttribute('aria-invalid', 'true');
      try {
        field.focus({ preventScroll: true });
      } catch (e) {
        /* see above */
      }
    }
  }

  function onSubmit(event) {
    const form = event.target.closest(FORM);
    if (!form || sending.has(form)) return;

    event.preventDefault();
    sending.add(form);
    form.classList.add('is-sending');
    const button = form.querySelector('[type="submit"]');
    if (button) button.disabled = true;

    const release = () => {
      sending.delete(form);
      if (button) button.disabled = false;
    };

    fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' },
    })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error('http'))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (doc.querySelector('[data-sb-newsletter-success]:not([hidden])')) return { ok: true };
        const returned = doc.querySelector('[data-sb-newsletter-error]:not([hidden])');
        if (returned) return { ok: false, message: returned.textContent.trim() };
        /* Neither marker came back — a bot challenge, an app-injected page, an
           interstitial. We cannot tell the reader anything true about it, so
           don't guess: fall through and let the native submit run. */
        return Promise.reject(new Error('unreadable'));
      })
      .then((result) => {
        if (result.ok) {
          succeed(form);
          return;
        }
        release();
        fail(form, result.message);
      })
      .catch(() => {
        /* Network, challenge or parser gave out. Hand the browser back its own
           form so the address still reaches Shopify — completing the signup
           beats staying on the page. Guarded so a throw inside succeed() can
           never post the same address twice. */
        if (form.classList.contains('is-subscribed')) return;
        release();
        form.classList.remove('is-sending');
        form.submit();
      });
  }

  document.addEventListener('submit', onSubmit);
})();
